/**
 * Wraps a fetch implementation so that concurrent calls cancel prior
 * in-flight requests via AbortController. Out-of-order responses surface
 * as a StaleResponseError that callers can ignore. Drop-in replacement for
 * react-storefront/utils/fetchLatest.
 */
export class StaleResponseError extends Error {
  constructor(...args) {
    super(...args)
    this.name = 'StaleResponseError'
  }
  static is(e) {
    return e && e.name === 'StaleResponseError'
  }
}

export function fetchLatest(fetchImpl) {
  let nextId = 0
  let controller

  const abort = () => {
    if (controller) controller.abort()
    if (typeof AbortController !== 'undefined') {
      controller = new AbortController()
      return controller
    }
    return { signal: null }
  }

  return (url, options) => {
    const id = ++nextId
    const signal = abort().signal
    return fetchImpl(url, { ...options, signal })
      .then(response => {
        if (id !== nextId) {
          throw new StaleResponseError()
        }
        return response
      })
      .catch(error => {
        if (error && error.name === 'AbortError') {
          throw new StaleResponseError()
        }
        throw error
      })
  }
}

export default fetchLatest
